import { useEffect, useRef } from "react"
import { useState } from "react"

import { getResource } from "../../api"

/**
 * @typedef Options
 * @property {{[key: string]: any}} filters
 * @property {{page: number; limit: number;}} pagination
 * @property {string} pageCountDataIndex
 * @property {boolean} triggerLoaderOnReload
 */

/**
 * @param {string} resourceUrl
 * @param {string} contentField
 * @param {Options} initialOptions
 * @param {string} authToken
 * @returns {[{[key: string]: any} | any[], {page: number; limit: number;}, () => void, (newFilters: any) => void, (newPagination: any) => void,  boolean, number]}
 */
export default function useRestList(
    resourceUrl, // Storeden sends back actual items in an embedded field. Specify that here
    token,
    initialOptions = {}
) {
    const contentField = "rows"
    const [resource, setResource] = useState(null)
    const [options, setOptions] = useState({
        filters: {},
        pagination: { page: 1, limit: 10 },
        ...initialOptions,
    })

    const [loading, setLoading] = useState(false)
    const [count, setCount] = useState()
    const { pageCountDataIndex = "product_per_page", executeLogs } =
        initialOptions
    const { triggerLoaderOnReload = true } = initialOptions

    const callCounter = useRef(0)

    useEffect(() => {
        fetchResource()
    }, [options])

    function getIdent(item) {
        if (typeof item === "object" && item !== null) {
            return item.id ?? item.uid ?? item.key
        } else return item
    }

    async function fetchResource() {
        // don't show loader if fetching next page
        callCounter.current = callCounter.current + 1
        const counter = callCounter.current
        triggerLoaderOnReload &&
            options.pagination.page === 1 &&
            setLoading(true)
        try {
            const url = buildUrl()
            // console.log("url", decodeURIComponent(url))
            const data = await getResource(url, token)
            resolvePayload(data, counter)
        } catch (e) {
            console.log(e)
        }
    }

    function resolvePayload(data, counter) {
        let newResource = []
        if (data && counter === callCounter.current) {
            executeLogs &&
                console.log("Writing call with call counter: ", counter)
            if (options.pagination.page === 1) {
                newResource = data[contentField]
            } else {
                const temp = [...(resource ?? []), ...data[contentField]]
                for (let i = 0; i < temp.length; i++) {
                    !newResource.find(
                        (item) => getIdent(item) === getIdent(temp[i])
                    ) && newResource.push(temp[i])
                }
            }
            setLoading(false)
            setResource(newResource)
            setCount(data?.count)
        } else {
            executeLogs &&
                console.log(
                    "Refusing to write data from call with call counter: ",
                    counter
                )
        }
    }

    function handleFiltersChange(newFilters) {
        setOptions({
            ...options,
            filters: { ...options.filters, ...newFilters },
            pagination: { page: 1, limit: options.pagination.limit },
        })
    }

    function handlePaginationChange(pagination) {
        setOptions({ ...options, pagination })
    }

    function buildUrl() {
        const filters = Object.entries(options.filters)
            .map(([key, filter]) => {
                if (filter) {
                    if (!(typeof filter === "object")) {
                        return `${encodeURIComponent(key)}=${encodeURIComponent(
                            filter
                        )}`
                    } else {
                        if (Array.isArray(filter)) {
                            return `${encodeURIComponent(
                                key
                            )}=${encodeURIComponent(filter.join(","))}`
                        } else {
                            return Object.entries(filter)
                                .map(
                                    ([subkey, subfilter]) =>
                                        `${encodeURIComponent(
                                            key
                                        )}[${encodeURIComponent(
                                            subkey
                                        )}]=${encodeURIComponent(subfilter)}`
                                )
                                .join("&")
                        }
                    }
                }
            })
            // remove undefined values
            .filter((i) => i)
            .join("&")
        const pagination = `page=${options.pagination.page}&${pageCountDataIndex}=${options.pagination.limit}`
        return `${resourceUrl}?${pagination}&${filters}`
    }

    return [
        resource ?? [],
        options?.pagination,
        fetchResource,
        handleFiltersChange,
        handlePaginationChange,
        loading,
        count,
    ]
}
